import React, { useMemo, useState } from 'react';
import { FiCheck, FiFile, FiSearch, FiX } from 'react-icons/fi';
import { DocumentItem } from '../types';
import './DocumentPickerModal.css';

interface DocumentPickerModalProps {
  open: boolean;
  documents: DocumentItem[];
  selectedIds: number[];
  isReady: (doc: DocumentItem) => boolean;
  onClose: () => void;
  onConfirm: (ids: number[]) => void;
}

// Multi-select document picker. Documents that aren't RAG-ready are still
// selectable (they work, just with lower-quality retrieval).
export default function DocumentPickerModal({
  open,
  documents,
  selectedIds,
  isReady,
  onClose,
  onConfirm,
}: DocumentPickerModalProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<number[]>(selectedIds);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? documents.filter((d) => d.name.toLowerCase().includes(q)) : documents;
  }, [documents, query]);

  if (!open) return null;

  const toggle = (id: number) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="picker-overlay" role="dialog" aria-modal="true" aria-label="Select documents" onClick={onClose}>
      <div className="picker" onClick={(e) => e.stopPropagation()}>
        <div className="picker__head">
          <h3 className="picker__title">Select documents</h3>
          <button className="picker__close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="picker__search">
          <FiSearch />
          <input
            className="input"
            placeholder="Search documents…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="picker__list">
          {filtered.length === 0 ? (
            <p className="muted picker__empty">No documents found.</p>
          ) : (
            filtered.map((doc) => {
              const active = selected.includes(doc.documentId);
              return (
                <button
                  key={doc.documentId}
                  className={`picker__item${active ? ' picker__item--active' : ''}`}
                  onClick={() => toggle(doc.documentId)}
                >
                  <span className="picker__itemIcon">
                    <FiFile />
                  </span>
                  <span className="picker__itemName" title={doc.name}>
                    {doc.name}
                  </span>
                  {isReady(doc) ? (
                    <span className="pill pill--success">Ready</span>
                  ) : (
                    <span className="pill pill--muted">Usable</span>
                  )}
                  <span className={`picker__check${active ? ' picker__check--on' : ''}`}>
                    {active && <FiCheck />}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="picker__foot">
          <span className="muted">{selected.length} selected</span>
          <div className="picker__footActions">
            <button className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn--primary" onClick={() => onConfirm(selected)} disabled={selected.length === 0}>
              Use {selected.length || ''} document{selected.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
